import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "../config";
import { useNavigate } from "react-router-dom";

/* ── Inject keyframes once ── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@300;400;500;600&display=swap');

  .ev-root *, .ev-root *::before, .ev-root *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .ev-root {
    --cream: #faf8f5;
    --white: #ffffff;
    --charcoal: #1a1a2e;
    --text-primary: #1c1c2e;
    --text-muted: #7a7a9a;
    --accent: #4f46e5;
    --accent-light: #eef2ff;
    --success: #059669;
    --success-light: #ecfdf5;
    --error: #dc2626;
    --error-light: #fef2f2;
    --border: #e8e4f0;
    --shadow-sm: 0 2px 8px rgba(79,70,229,0.06);
    --shadow-lg: 0 24px 64px rgba(79,70,229,0.14);
    --radius: 24px;

    font-family: 'DM Sans', sans-serif;
    background: var(--cream);
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    position: relative;
    overflow: hidden;
  }

  .ev-root::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse 80% 60% at 20% 10%, rgba(79,70,229,0.07) 0%, transparent 60%),
      radial-gradient(ellipse 60% 50% at 85% 90%, rgba(16,185,129,0.06) 0%, transparent 55%),
      radial-gradient(ellipse 50% 40% at 70% 15%, rgba(245,158,11,0.04) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }

  /* Floating dots */
  .ev-dot {
    position: fixed;
    border-radius: 50%;
    pointer-events: none;
    z-index: 0;
    animation: ev-floatDot linear infinite;
  }
  .ev-dot:nth-child(1) { width:6px; height:6px; background:#4f46e5; left:12%; top:20%; opacity:.5; animation-duration:14s; }
  .ev-dot:nth-child(2) { width:4px; height:4px; background:#059669; left:80%; top:15%; opacity:.4; animation-duration:18s; animation-delay:-4s; }
  .ev-dot:nth-child(3) { width:8px; height:8px; background:#f59e0b; left:65%; top:75%; opacity:.4; animation-duration:12s; animation-delay:-7s; }
  .ev-dot:nth-child(4) { width:5px; height:5px; background:#4f46e5; left:25%; top:80%; opacity:.45; animation-duration:16s; animation-delay:-2s; }
  .ev-dot:nth-child(5) { width:3px; height:3px; background:#ec4899; left:50%; top:10%; opacity:.4; animation-duration:20s; animation-delay:-9s; }

  @keyframes ev-floatDot {
    0%   { transform: translateY(0)    scale(1);   opacity: .5; }
    50%  { transform: translateY(-30px) scale(1.2); opacity: .8; }
    100% { transform: translateY(0)    scale(1);   opacity: .5; }
  }

  /* Wrapper */
  .ev-wrapper { width: 100%; max-width: 480px; position: relative; z-index: 1; }

  /* Brand */
  .ev-brand {
    text-align: center;
    margin-bottom: 28px;
    animation: ev-fadeDown 0.6s ease both;
  }
  .ev-brand-name {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--charcoal);
    letter-spacing: -0.02em;
  }
  .ev-brand-dot { color: var(--accent); }

  /* Card */
  .ev-card {
    background: var(--white);
    border-radius: var(--radius);
    box-shadow: var(--shadow-lg);
    border: 1px solid var(--border);
    padding: 52px 48px;
    position: relative;
    overflow: hidden;
    animation: ev-cardReveal 0.7s cubic-bezier(0.22,1,0.36,1) both 0.1s;
  }
  .ev-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #4f46e5, #818cf8, #a78bfa);
    border-radius: var(--radius) var(--radius) 0 0;
  }

  @keyframes ev-cardReveal {
    from { opacity: 0; transform: translateY(28px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0)    scale(1); }
  }
  @keyframes ev-fadeDown {
    from { opacity: 0; transform: translateY(-12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes ev-fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* Back button */
  .ev-back {
    position: absolute;
    top: 20px; left: 20px;
    width: 36px; height: 36px;
    border-radius: 10px;
    border: 1.5px solid var(--border);
    background: var(--white);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: var(--text-muted);
    box-shadow: var(--shadow-sm);
  }
  .ev-back:hover {
    background: var(--accent-light);
    border-color: var(--accent);
    color: var(--accent);
    transform: translateX(-2px);
  }

  /* State panels */
  .ev-state { display: flex; flex-direction: column; align-items: center; text-align: center; animation: ev-fadeUp 0.5s ease both 0.3s; }

  /* Icon ring */
  .ev-icon-ring {
    width: 96px; height: 96px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 28px;
    position: relative;
  }
  .ev-icon-ring.loading { background: var(--accent-light); }
  .ev-icon-ring.success { background: var(--success-light); }
  .ev-icon-ring.error   { background: var(--error-light); }

  .ev-icon-ring.loading::after {
    content: '';
    position: absolute; inset: -6px;
    border-radius: 50%;
    border: 2px solid rgba(79,70,229,0.15);
    animation: ev-pulseRing 1.8s ease-out infinite;
  }
  @keyframes ev-pulseRing {
    0%   { transform: scale(1);   opacity: .7; }
    100% { transform: scale(1.3); opacity: 0; }
  }

  .ev-icon-ring.error { animation: ev-shake 0.5s ease 0.3s both; }
  @keyframes ev-shake {
    0%,100% { transform: translateX(0); }
    20%     { transform: translateX(-6px); }
    40%     { transform: translateX(6px); }
    60%     { transform: translateX(-4px); }
    80%     { transform: translateX(4px); }
  }

  /* Spinner */
  .ev-spinner {
    width: 44px; height: 44px;
    border: 3px solid rgba(79,70,229,0.15);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: ev-spin 0.9s linear infinite;
  }
  @keyframes ev-spin { to { transform: rotate(360deg); } }

  /* SVG checkmark */
  .ev-checkmark-circle {
    stroke-dasharray: 166;
    stroke-dashoffset: 166;
    stroke-width: 2.5;
    stroke: var(--success);
    fill: none;
    animation: ev-stroke 0.6s cubic-bezier(0.65,0,0.45,1) 0.4s forwards;
  }
  .ev-checkmark-path {
    stroke-dasharray: 48;
    stroke-dashoffset: 48;
    stroke: var(--success);
    stroke-width: 2.5;
    fill: none;
    animation: ev-stroke 0.3s cubic-bezier(0.65,0,0.45,1) 0.9s forwards;
  }
  @keyframes ev-stroke { 100% { stroke-dashoffset: 0; } }

  /* Typography */
  .ev-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.75rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin-bottom: 10px;
    line-height: 1.2;
  }
  .ev-title.loading { color: var(--text-primary); }
  .ev-title.success { color: var(--success); }
  .ev-title.error   { color: var(--error); }

  .ev-sub {
    font-size: 0.95rem;
    color: var(--text-muted);
    font-weight: 400;
    line-height: 1.6;
    max-width: 320px;
    margin: 0 auto 32px;
  }

  /* Loading dots */
  .ev-loading-dots { display: flex; gap: 6px; justify-content: center; margin-bottom: 28px; }
  .ev-ldot {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--accent); opacity: 0.25;
    animation: ev-stepPulse 1.4s ease-in-out infinite;
  }
  .ev-ldot:nth-child(2) { animation-delay: .2s; }
  .ev-ldot:nth-child(3) { animation-delay: .4s; }
  @keyframes ev-stepPulse {
    0%,80%,100% { opacity: .25; transform: scale(1); }
    40%         { opacity: 1;   transform: scale(1.35); }
  }

  /* Alert */
  .ev-alert {
    background: var(--error-light);
    border: 1px solid rgba(220,38,38,0.15);
    border-radius: 14px;
    padding: 16px 18px;
    text-align: left;
    margin-bottom: 28px;
    width: 100%;
  }
  .ev-alert p { font-size: .875rem; color: var(--error); font-weight: 500; line-height: 1.5; }
  .ev-alert .ev-alert-hint { font-size: .8rem; margin-top: 4px; opacity: .75; font-weight: 400; }

  /* Buttons */
  .ev-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    padding: 14px 36px;
    border-radius: 14px;
    font-family: 'DM Sans', sans-serif;
    font-size: .975rem; font-weight: 600;
    cursor: pointer; transition: all .22s ease;
    text-decoration: none; border: none;
    letter-spacing: .01em;
  }
  .ev-btn-primary {
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    color: #fff;
    box-shadow: 0 4px 16px rgba(79,70,229,.3), 0 1px 3px rgba(79,70,229,.2);
  }
  .ev-btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(79,70,229,.38), 0 2px 6px rgba(79,70,229,.22);
  }
  .ev-btn-outline {
    background: transparent; color: var(--accent);
    border: 1.5px solid rgba(79,70,229,.3);
  }
  .ev-btn-outline:hover { background: var(--accent-light); border-color: var(--accent); transform: translateY(-1px); }

  /* Footer */
  .ev-footer {
    text-align: center; margin-top: 28px;
    font-size: .8rem; color: var(--text-muted);
    animation: ev-fadeDown 0.6s ease both 0.5s;
    letter-spacing: .01em;
  }
  .ev-footer a { color: var(--accent); text-decoration: none; }
  .ev-footer a:hover { text-decoration: underline; }

  /* Responsive */
  @media (max-width: 520px) {
    .ev-card { padding: 44px 28px; }
    .ev-title { font-size: 1.5rem; }
  }
  @media (max-width: 360px) {
    .ev-card { padding: 36px 20px; }
  }
`;

/* ── SVG Icons ── */
const ArrowLeftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const LoginIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" />
    <line x1="15" y1="12" x2="3" y2="12" />
  </svg>
);

const MailIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const ErrorIcon = () => (
  <svg
    width="44"
    height="44"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#dc2626"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

/* ── Sub-components ── */
const LoadingState = () => (
  <div className="ev-state">
    <div className="ev-icon-ring loading">
      <div className="ev-spinner" />
    </div>
    <h2 className="ev-title loading">Verifying your email</h2>
    <p className="ev-sub">
      Please wait while we securely confirm your account. This takes just a
      moment.
    </p>
    <div className="ev-loading-dots">
      <div className="ev-ldot" />
      <div className="ev-ldot" />
      <div className="ev-ldot" />
    </div>
  </div>
);

const SuccessState = ({ message, onLogin }) => (
  <div className="ev-state">
    <div className="ev-icon-ring success">
      <svg width="52" height="52" viewBox="0 0 52 52">
        <circle className="ev-checkmark-circle" cx="26" cy="26" r="25" />
        <path
          className="ev-checkmark-path"
          d="M14 27l8 8 16-16"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
    <h2 className="ev-title success">Email Verified!</h2>
    <p className="ev-sub">
      {message ||
        "Your email has been successfully verified. You can now log in and start using LiteUpper."}
    </p>
    <button className="ev-btn ev-btn-primary" onClick={onLogin}>
      <LoginIcon /> Go to Login
    </button>
  </div>
);

const ErrorState = ({ message }) => (
  <div className="ev-state">
    <div className="ev-icon-ring error">
      <ErrorIcon />
    </div>
    <h2 className="ev-title error">Verification Failed</h2>
    <div className="ev-alert">
      <p>
        {message || "Verification failed. The link may be expired or invalid."}
      </p>
      <p className="ev-alert-hint">
        Each link is single-use and expires after 24 hours.
      </p>
    </div>
    <p className="ev-sub" style={{ marginBottom: 16 }}>
      Need a new link or help? Our support team is here for you.
    </p>
    <a href="mailto:support@liteupper.com" className="ev-btn ev-btn-outline">
      <MailIcon /> Contact Support
    </a>
  </div>
);

/* ── Main Component ── */
const EmailVerificationPage = () => {
  const [status, setStatus] = useState("loading"); // 'loading' | 'success' | 'error'
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Inject styles
    if (!document.getElementById("ev-styles")) {
      const style = document.createElement("style");
      style.id = "ev-styles";
      style.textContent = STYLES;
      document.head.appendChild(style);
    }

    // Verify token
    const verifyEmail = async () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");

      if (!token) {
        setStatus("error");
        setMessage("Invalid or missing verification token.");
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/verify?token=${encodeURIComponent(token)}`
        );
        if (response.status === 200) {
          setStatus("success");
          setMessage("Your email has been successfully verified!");
        }
      } catch (error) {
        console.error("Verification failed:", error);
        setStatus("error");
        setMessage(
          error.response?.data?.detail ||
            "Verification failed. The link may be expired or invalid."
        );
      }
    };

    verifyEmail();
  }, []);

  return (
    <div className="ev-root">
      {/* Background floating dots */}
      <div className="ev-dot" />
      <div className="ev-dot" />
      <div className="ev-dot" />
      <div className="ev-dot" />
      <div className="ev-dot" />

      <div className="ev-wrapper">
        {/* Brand */}
        <div className="ev-brand">
          <span className="ev-brand-name">
            Lite<span className="ev-brand-dot">Upper</span>
          </span>
        </div>

        {/* Card */}
        <div className="ev-card">
          {/* Back button */}
          <button
            className="ev-back"
            onClick={() => navigate(-1)}
            title="Go back"
          >
            <ArrowLeftIcon />
          </button>

          {/* States */}
          {status === "loading" && <LoadingState />}
          {status === "success" && (
            <SuccessState
              message={message}
              onLogin={() => navigate("/login")}
            />
          )}
          {status === "error" && <ErrorState message={message} />}
        </div>

        {/* Footer */}
        <div className="ev-footer">
          © {new Date().getFullYear()} LiteUpper &nbsp;·&nbsp; Secure &amp;
          Trusted &nbsp;·&nbsp;
          {/* <a href="#">Privacy Policy</a> */}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
